import React from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useDepositSettings } from '../deposits/useDepositSettings';
import GradientButton from '../../buttons/GradientButton';
import BackToPaymentSettings from '../components/BackToPaymentSettings';
import './payment-subsettings/PaymentSubsettings.css';

const AlertIconWarning = () => (
  <svg className="ps-alert__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      fill="currentColor"
      d="M12 3L1 21h22L12 3zm0 3.47L18.53 19H5.47L12 6.47zM11 10h2v4h-2v-4zm0 6h2v2h-2v-2z"
    />
  </svg>
);

const DepositSettings = ({ onBackToPayments, embedded = false }) => {
  const { companyData, updateCompanyData } = useAuth();
  const { setNotification } = useNotification();

  const hasPaymentProcessor = Boolean(
    companyData?.deluxe?.connection?.connected === true
  );

  const {
    settings,
    setSettings,
    saveSettings,
    isLoading
  } = useDepositSettings(companyData, updateCompanyData, setNotification);

  const handleChange = (section, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const getCalculationExample = () => {
    const type = settings?.defaultSettings?.calculationType;
    const amount = settings?.defaultSettings?.amount || 0;

    switch (type) {
      case 'duration':
        return `For a 2-hour service, the deposit would be $${amount * 2}`;
      case 'percentage':
        return `For a $100 service, the deposit would be $${amount}`;
      case 'fixed':
        return `All services will require a $${amount} deposit`;
      default:
        return '';
    }
  };

  const amountLabel =
    settings?.defaultSettings?.calculationType === 'duration'
      ? 'Amount per hour'
      : settings?.defaultSettings?.calculationType === 'percentage'
        ? 'Percentage'
        : 'Fixed amount';

  if (!settings) {
    return (
      <div className={`ps-root ${embedded ? 'ps-root--embedded' : ''}`}>
        <div className="ps-loading">
          <div className="ps-skeleton" aria-hidden />
          <p className="ps-muted" style={{ marginTop: '1rem' }}>Loading deposit settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ps-root ${embedded ? 'ps-root--embedded' : ''}`}>
      {onBackToPayments && !embedded && <BackToPaymentSettings onBack={onBackToPayments} />}

      {!hasPaymentProcessor && (
        <div className="ps-alert ps-alert--warning" role="status">
          <AlertIconWarning />
          <div>
            You need to set up a payment processor before you can enable deposits. Connect Deluxe
            under Payment gateways above.
          </div>
        </div>
      )}

      <div className="ps-option-with-help">
        <label className="ps-option-box" htmlFor="deposit-global-enabled">
          <input
            id="deposit-global-enabled"
            type="checkbox"
            className="ps-checkbox"
            checked={settings.globalEnabled || false}
            disabled={!hasPaymentProcessor}
            aria-describedby="deposit-enable-hint"
            onChange={async (e) => {
              if (e.target.checked && !hasPaymentProcessor) {
                setNotification({
                  open: true,
                  message: 'Please set up a payment processor before enabling deposits',
                  severity: 'error'
                });
                return;
              }
              const newSettings = {
                ...settings,
                globalEnabled: e.target.checked
              };
              setSettings(newSettings);
              await saveSettings(newSettings);
            }}
          />
          <span className="ps-option-box__body">
            <span className="ps-option-box__title">Enable deposits</span>
            <p id="deposit-enable-hint" className="ps-option-box__hint">
              Tick the box to require a deposit on bookings. Defaults apply to all services unless you override them per
              service.
            </p>
          </span>
        </label>
        <button
          type="button"
          className="ps-help-btn"
          onMouseDown={(e) => e.preventDefault()}
          title={
            hasPaymentProcessor
              ? "Turn on deposits to secure bookings. You can customize deposit amounts in each service's settings under Manage Services."
              : 'You need to set up a payment processor before enabling deposits.'
          }
          aria-label="More about enabling deposits"
        >
          ?
        </button>
      </div>

      {settings.globalEnabled && (
        <>
          <div className="ps-panel">
            <p className="ps-panel__title">How deposits work</p>
            <p>
              <strong>1.</strong> Default: all services start with your default rule below.
            </p>
            <p>
              <strong>2.</strong> Per service: Manage Services → edit a service → deposit settings.
            </p>
            <p>
              Example: 30% for premium services, a fixed $50 for standard, or amount based on duration.
            </p>
          </div>

          <h4 className="ps-section-head">
            Default calculation
            <button
              type="button"
              className="ps-help-btn"
              title="Defaults apply to every service until you override them in Manage Services."
              aria-label="Help: default calculation"
            >
              ?
            </button>
          </h4>

          <div className="ps-form-grid">
            <div className="ps-field">
              <label htmlFor="deposit-calc-type">Calculation method</label>
              <select
                id="deposit-calc-type"
                className="ps-select"
                value={settings.defaultSettings?.calculationType || 'duration'}
                onChange={(e) => handleChange('defaultSettings', 'calculationType', e.target.value)}
              >
                <option value="duration">Based on duration</option>
                <option value="fixed">Fixed amount</option>
                <option value="percentage">Percentage of price</option>
              </select>
            </div>
            <div className="ps-field">
              <label htmlFor="deposit-amount">{amountLabel}</label>
              {settings?.defaultSettings?.calculationType === 'percentage' ? (
                <div className="ps-input-wrap">
                  <input
                    id="deposit-amount"
                    type="number"
                    className="ps-input"
                    min={0}
                    step={0.01}
                    value={settings.defaultSettings?.amount ?? 0}
                    onChange={(e) =>
                      handleChange('defaultSettings', 'amount', Number(e.target.value))
                    }
                  />
                  <span className="ps-input-suffix">%</span>
                </div>
              ) : (
                <input
                  id="deposit-amount"
                  type="number"
                  className="ps-input"
                  min={0}
                  step={0.01}
                  value={settings.defaultSettings?.amount ?? 0}
                  onChange={(e) => handleChange('defaultSettings', 'amount', Number(e.target.value))}
                />
              )}
            </div>
          </div>

          <div className="ps-example">
            <div className="ps-example__label">Example</div>
            <p className="ps-example__text">{getCalculationExample()}</p>
          </div>

          <div className="ps-form-grid ps-form-grid--tight">
            <div className="ps-field">
              <label htmlFor="deposit-min">Minimum deposit</label>
              <input
                id="deposit-min"
                type="number"
                className="ps-input"
                min={0}
                step={0.01}
                value={settings.defaultSettings?.minimumAmount ?? 0}
                onChange={(e) =>
                  handleChange('defaultSettings', 'minimumAmount', Number(e.target.value))
                }
              />
              <p className="ps-field-hint">Lowest acceptable deposit</p>
            </div>
            <div className="ps-field">
              <label htmlFor="deposit-max">Maximum deposit</label>
              <input
                id="deposit-max"
                type="number"
                className="ps-input"
                min={0}
                step={0.01}
                value={settings.defaultSettings?.maximumAmount ?? 0}
                onChange={(e) =>
                  handleChange('defaultSettings', 'maximumAmount', Number(e.target.value))
                }
              />
              <p className="ps-field-hint">Highest acceptable deposit</p>
            </div>
          </div>

          <hr className="ps-rule" />

          <h4 className="ps-section-head">
            Cancellation and refund policy
            <button
              type="button"
              className="ps-help-btn"
              title="Shown to clients during booking when a deposit is required"
              aria-label="Help: cancellation and refund policy"
            >
              ?
            </button>
          </h4>

          <div className="ps-field" style={{ marginBottom: '1rem' }}>
            <label htmlFor="deposit-refund-policy">Cancellation and refund policy</label>
            <textarea
              id="deposit-refund-policy"
              className="ps-textarea"
              rows={4}
              value={settings.cancellationPolicy?.refundPolicy || ''}
              onChange={(e) => handleChange('cancellationPolicy', 'refundPolicy', e.target.value)}
              placeholder="e.g. Full refund if cancelled 24 hours before. No refund for late cancellations or no-shows."
            />
            <p className="ps-field-hint">
              Explain your deposit refund rules, including how far in advance clients must cancel
            </p>
          </div>

          <div className="ps-actions ps-actions--flush">
            <GradientButton variant="primary" className="ps-save-btn" onClick={() => saveSettings(settings)} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="ps-btn-spinner" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </GradientButton>
          </div>
        </>
      )}

    </div>
  );
};

export default DepositSettings;
